import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

import type { EditableQuestionPart } from './editorTypes';
import { AIUploadQuestionEditor } from './aiUpload/AIUploadQuestionEditor';
import { ExactAnswerEditor } from './ExactAnswerEditor';
import { HintSolutionEditor } from './HintSolutionEditor';
import { MultipleChoiceEditor } from './MultipleChoiceEditor';
import { NumericalAnswerEditor } from './NumericalAnswerEditor';
import {
  createDraftGradingRubric,
  getRubricTotalScore,
} from './questionDialogUtils';
import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import { TextUnitEditor } from './TextUnitEditor';
import styles from './QuestionPartCard.module.css';

interface QuestionPartCardProps {
  disabled?: boolean;
  index: number;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onRemove: () => void;
  onUpdate: (updates: Partial<EditableQuestionPart>) => void;
  part: EditableQuestionPart;
  structuralEditsRestricted?: boolean;
  totalParts: number;
  useRandomVariables?: boolean;
}

// Capital letters, as students see their parts: "Part A", "Part B".
function getPartLabel(index: number) {
  return String.fromCharCode('A'.charCodeAt(0) + index);
}

function getTypeDefaults(
  type: EditableQuestionPart['type'],
  currentPart: EditableQuestionPart,
): Partial<EditableQuestionPart> {
  if (type === 'MULTIPLE_CHOICE') {
    return {
      gradingRubric: undefined,
      maxNumAttempts: undefined,
      correctAnswerIndex:
        currentPart.correctAnswerIndex ??
        currentPart.correctAnswerIndices?.[0] ??
        0,
      options: currentPart.options.length >= 2 ? currentPart.options : ['', ''],
      type,
      useCustomNumAttempts: undefined,
    };
  }

  if (type === 'SELECT_ALL') {
    return {
      // Switching from multiple choice carries its marked option over.
      correctAnswerIndices:
        currentPart.correctAnswerIndices &&
        currentPart.correctAnswerIndices.length > 0
          ? currentPart.correctAnswerIndices
          : currentPart.correctAnswerIndex === undefined
            ? []
            : [currentPart.correctAnswerIndex],
      gradingRubric: undefined,
      maxNumAttempts: undefined,
      options: currentPart.options.length >= 2 ? currentPart.options : ['', ''],
      type,
      useCustomNumAttempts: undefined,
    };
  }

  if (type === 'NUMERICAL_WITH_ERROR') {
    return {
      gradingRubric: undefined,
      maxNumAttempts: undefined,
      type,
      useCustomNumAttempts: undefined,
    };
  }

  if (type === 'AI_UPLOAD') {
    const gradingRubric = currentPart.gradingRubric ?? createDraftGradingRubric();

    return {
      answerUnits: undefined,
      correctAnswerIndex: undefined,
      exactAnswer: undefined,
      gradingRubric,
      maxNumAttempts: currentPart.maxNumAttempts ?? 1,
      numericalAnswer: undefined,
      numericalError: undefined,
      options: [],
      type,
      useCustomNumAttempts: true,
      value: getRubricTotalScore(gradingRubric),
      variableBasedAnswer: undefined,
    };
  }

  return {
    gradingRubric: undefined,
    maxNumAttempts: undefined,
    type,
    useCustomNumAttempts: undefined,
  };
}

export function QuestionPartCard({
  disabled = false,
  index,
  onMoveDown,
  onMoveUp,
  onRemove,
  onUpdate,
  part,
  structuralEditsRestricted = false,
  totalParts,
  useRandomVariables = false,
}: QuestionPartCardProps) {
  const label = getPartLabel(index);
  const structuralDisabled = disabled || structuralEditsRestricted;

  return (
    <li className={styles.root}>
      <div className={styles.header}>
        <h4 className={styles.title}>Part {label}</h4>

        <div className={styles.headerActions}>
          <button
            aria-label={`Move part ${label} up`}
            className={styles.iconButton}
            disabled={structuralDisabled || index === 0}
            onClick={onMoveUp}
            title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
            type="button"
          >
            <ChevronUp aria-hidden="true" size={16} strokeWidth={2} />
          </button>
          <button
            aria-label={`Move part ${label} down`}
            className={styles.iconButton}
            disabled={structuralDisabled || index === totalParts - 1}
            onClick={onMoveDown}
            title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
            type="button"
          >
            <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />
          </button>
          <button
            aria-label={`Delete part ${label}`}
            className={styles.dangerIcon}
            disabled={structuralDisabled}
            onClick={onRemove}
            title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
            type="button"
          >
            <Trash2 aria-hidden="true" size={16} strokeWidth={2} />
          </button>
        </div>
        <label className={styles.typeField}>
          <span className={styles.label}>Part Type</span>
          <select
            className={styles.select}
            disabled={structuralDisabled}
            onChange={(event) => {
              onUpdate(
                getTypeDefaults(
                  event.target.value as EditableQuestionPart['type'],
                  part,
                ),
              );
            }}
            title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
            value={part.type}
          >
            <option value="MULTIPLE_CHOICE">Multiple Choice</option>
            <option value="SELECT_ALL">Select All That Apply</option>
            <option value="NUMERICAL_WITH_ERROR">Numerical</option>
            <option value="EXACT_ANSWER">Exact Answer</option>
            <option value="AI_UPLOAD">AI Upload</option>
          </select>
        </label>
      </div>

      <div className={styles.editorGroup}>
        <p className={styles.sectionLabel}>Prompt</p>
        <TextUnitEditor
          label={`Part ${label} prompt`}
          onChange={(contentHtml) => {
            onUpdate({ contentHtml });
          }}
          placeholder="Write this part's question…"
          value={part.contentHtml}
        />
      </div>

      {part.type === 'MULTIPLE_CHOICE' || part.type === 'SELECT_ALL' ? (
        <MultipleChoiceEditor
          correctAnswerIndex={part.correctAnswerIndex}
          correctAnswerIndices={part.correctAnswerIndices}
          disabled={disabled}
          groupName={`question-part-${part.id}`}
          multiSelect={part.type === 'SELECT_ALL'}
          onAddOption={() => {
            onUpdate({
              options: [...part.options, ''],
            });
          }}
          onChangeCorrectAnswer={(index) => {
            if (part.type !== 'SELECT_ALL') {
              onUpdate({ correctAnswerIndex: index });
              return;
            }

            const current = part.correctAnswerIndices ?? [];
            onUpdate({
              correctAnswerIndices: current.includes(index)
                ? current.filter((entry) => entry !== index)
                : [...current, index].sort((a, b) => a - b),
            });
          }}
          onChangeOption={(optionIndex, value) => {
            onUpdate({
              options: part.options.map((option, currentIndex) =>
                currentIndex === optionIndex ? value : option,
              ),
            });
          }}
          onRemoveOption={(optionIndex) => {
            const nextOptions = part.options.filter(
              (_, currentIndex) => currentIndex !== optionIndex,
            );
            const nextCorrectAnswerIndex =
              part.correctAnswerIndex === undefined
                ? undefined
                : part.correctAnswerIndex === optionIndex
                  ? 0
                  : part.correctAnswerIndex > optionIndex
                    ? part.correctAnswerIndex - 1
                    : part.correctAnswerIndex;

            const nextCorrectAnswerIndices = (part.correctAnswerIndices ?? [])
              .filter((entry) => entry !== optionIndex)
              .map((entry) => (entry > optionIndex ? entry - 1 : entry));

            onUpdate({
              correctAnswerIndex: nextCorrectAnswerIndex,
              correctAnswerIndices: nextCorrectAnswerIndices,
              options: nextOptions,
            });
          }}
          options={part.options}
          structuralEditsRestricted={structuralEditsRestricted}
        />
      ) : null}

      {part.type === 'NUMERICAL_WITH_ERROR' ? (
        <NumericalAnswerEditor
          answerUnits={part.answerUnits}
          disabled={disabled}
          numericalAnswer={part.numericalAnswer}
          numericalError={part.numericalError}
          onChange={onUpdate}
          structuralEditsRestricted={structuralEditsRestricted}
          useRandomVariables={useRandomVariables}
          variableBasedAnswer={part.variableBasedAnswer}
        />
      ) : null}

      {part.type === 'EXACT_ANSWER' ? (
        <ExactAnswerEditor
          answerUnits={part.answerUnits}
          disabled={disabled}
          exactAnswer={part.exactAnswer}
          onChange={onUpdate}
          structuralEditsRestricted={structuralEditsRestricted}
          useRandomVariables={useRandomVariables}
          variableBasedAnswer={part.variableBasedAnswer}
        />
      ) : null}

      {part.type === 'AI_UPLOAD' ? (
        <AIUploadQuestionEditor
          disabled={disabled}
          gradingRubric={part.gradingRubric}
          onChange={(gradingRubric) => {
            onUpdate({
              gradingRubric,
              value: getRubricTotalScore(gradingRubric),
            });
          }}
          validationError={part.validationError}
        />
      ) : null}

      <label className={styles.field}>
        <span className={styles.label}>Points</span>
        {/* Named for its part: the question's own field is "Points". */}
        <input
          aria-label={`Part ${label} points`}
          className={styles.input}
          disabled={disabled || part.type === 'AI_UPLOAD'}
          onChange={(event) => {
            onUpdate({
              value: event.target.value === '' ? undefined : Number(event.target.value),
            });
          }}
          min="0"
          step="0.5"
          type="number"
          value={part.value ?? ''}
        />
      </label>

      <HintSolutionEditor
        detailedAnswerHtml={part.detailedAnswerHtml}
        disabled={disabled}
        hintHtml={part.hintHtml}
        onDetailedAnswerChange={(detailedAnswerHtml) => {
          onUpdate({ detailedAnswerHtml });
        }}
        onHintChange={(hintHtml) => {
          onUpdate({ hintHtml });
        }}
      />

      {part.validationError ? (
        <p className={styles.errorMessage} role="alert">
          {part.validationError}
        </p>
      ) : null}
    </li>
  );
}
